import type { ElementType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ChipOption<V extends string = string> {
  value: V;
  label: string;
  icon?: ElementType;
  /** Tint classes; a tinted chip dims when unselected and gets a ring when selected. */
  className?: string;
  title?: string;
}

/** Uppercase heading for a group in a report filter card. */
export function FacetLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  );
}

export function Chip({ selected, onClick, icon: Icon, tint, title, count, children }: {
  selected: boolean;
  onClick: () => void;
  icon?: ElementType;
  tint?: string;
  title?: string;
  count?: number;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={selected}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
        tint
          ? cn('border-transparent', tint, selected ? 'ring-2 ring-primary/50 ring-offset-1' : 'opacity-60 hover:opacity-100')
          : (selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground'),
      )}
    >
      {Icon && <Icon className="size-3" />}
      {children}
      {count !== undefined && <span className="tabular-nums opacity-70">{count}</span>}
    </button>
  );
}

export function ChipGroup<V extends string>({
  label,
  options,
  selected,
  onToggle,
  counts,
  all,
}: {
  label: string;
  options: ReadonlyArray<ChipOption<V>>;
  /** A Set or array for multi-select, or a single value for single-select. */
  selected: ReadonlySet<V> | readonly V[] | V;
  onToggle: (value: V) => void;
  counts?: Partial<Record<V, number>>;
  /** Leading "All" chip, selected when nothing else is unless `selected` is given. */
  all?: { onClick: () => void; count?: number; title?: string; selected?: boolean };
}) {
  const isSelected = (value: V) => {
    if (typeof selected === 'string')
      return selected === value;
    return selected instanceof Set ? selected.has(value) : (selected as readonly V[]).includes(value);
  };
  const noneSelected = typeof selected === 'string'
    ? selected === ''
    : (selected instanceof Set ? selected.size : (selected as readonly V[]).length) === 0;

  return (
    <div className="flex flex-col gap-1.5">
      <FacetLabel>{label}</FacetLabel>
      <div className="flex flex-wrap gap-1.5">
        {all && (
          <Chip selected={all.selected ?? noneSelected} onClick={all.onClick} title={all.title} count={all.count}>
            All
          </Chip>
        )}
        {options.map((opt) => (
          <Chip
            key={opt.value}
            selected={isSelected(opt.value)}
            onClick={() => onToggle(opt.value)}
            icon={opt.icon}
            tint={opt.className}
            title={opt.title}
            count={counts ? (counts[opt.value] ?? 0) : undefined}
          >
            {opt.label}
          </Chip>
        ))}
      </div>
    </div>
  );
}
